import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Youtube, Instagram, Play } from "lucide-react";
import { SOCIAL } from "@/config/social";
import { INSTAGRAM_POSTS } from "@/config/instagramPosts";
import { InstagramEmbed } from "@/components/InstagramEmbed";
import { trackCta } from "@/lib/trackCta";
import { supabase } from "@/integrations/supabase/client";

type Video = {
  id: string;
  title: string;
  url: string;
  thumbnail: string;
  publishedAt: string;
};

const useYouTubeVideos = (limit = 6) => {
  const [videos, setVideos] = useState<Video[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("fetch-youtube-feed", {
          body: { limit },
        });
        if (cancelled) return;
        if (error) throw error;
        const payload = data as { videos?: Video[] };
        setVideos(payload?.videos ?? []);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [limit]);

  return { videos, error };
};

export const SocialFeed = () => {
  const { videos, error } = useYouTubeVideos(6);
  const loading = videos === null && !error;
  const instagramPosts = INSTAGRAM_POSTS;

  return (
    <div className="max-w-5xl mx-auto space-y-20">
      {/* YouTube */}
      <div>
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-foreground">
              On <span className="text-accent">YouTube</span>
            </h3>
            <p className="text-muted-foreground mt-2">Latest videos, auto-pulled.</p>
          </div>
          <a
            href={SOCIAL.youtube.channelUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-accent hover:underline"
            aria-label={`Open @${SOCIAL.youtube.handle} on YouTube (opens in a new tab)`}
            onClick={() => trackCta("home_content_youtube", SOCIAL.youtube.channelUrl, `@${SOCIAL.youtube.handle}`)}
          >
            @{SOCIAL.youtube.handle} →
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        </div>

        {loading && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-video rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        )}

        {!loading && videos && videos.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {videos.map((v) => (
              <a
                key={v.id}
                href={v.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Watch: ${v.title} (opens in a new tab)`}
                onClick={() => trackCta("home_content_reel", v.url, v.title)}
                className="group block rounded-xl overflow-hidden bg-card shadow-md hover:shadow-bold transition-all duration-300 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
              >
                <div className="relative aspect-video bg-muted">
                  <img
                    src={v.thumbnail}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
                    <div className="w-12 h-12 rounded-full bg-[#FF0000] flex items-center justify-center shadow-lg">
                      <Play className="w-5 h-5 text-white fill-white ml-0.5" />
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-sm font-medium text-foreground line-clamp-2 leading-snug">
                    {v.title}
                  </p>
                </div>
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            ))}
          </div>
        )}

        {!loading && (error || (videos && videos.length === 0)) && (
          <div className="rounded-xl border border-border bg-card p-8 text-center text-muted-foreground">
            New videos will appear here.{" "}
            <a
              href={SOCIAL.youtube.channelUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:underline"
            >
              Watch on YouTube
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
          </div>
        )}

        <div className="text-center mt-10">
          <Button
            size="lg"
            asChild
            className="bg-[#FF0000] text-white hover:bg-[#CC0000] gap-2 shadow-lg"
          >
            <a
              href={SOCIAL.youtube.channelUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCta("home_content_youtube", SOCIAL.youtube.channelUrl, "Follow on YouTube")}
            >
              <Youtube className="w-5 h-5" />
              Follow on YouTube
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
          </Button>
        </div>
      </div>

      {/* Instagram */}
      <div>
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-foreground">
              On <span className="text-accent">Instagram</span>
            </h3>
            <p className="text-muted-foreground mt-2">Latest posts and reels.</p>
          </div>
          <a
            href={SOCIAL.instagram.profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-accent hover:underline"
            aria-label={`Open @${SOCIAL.instagram.handle} on Instagram (opens in a new tab)`}
            onClick={() => trackCta("home_content_instagram_header", SOCIAL.instagram.profileUrl, `@${SOCIAL.instagram.handle}`)}
          >
            @{SOCIAL.instagram.handle} →
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        </div>

        {instagramPosts.length > 0 ? (
          <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
            {instagramPosts.map((url) => (
              <InstagramEmbed key={url} url={url} />
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card p-8 text-center text-muted-foreground">
            Latest posts will appear here.{" "}
            <a
              href={SOCIAL.instagram.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:underline"
            >
              View on Instagram
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
          </div>
        )}

        <div className="text-center mt-10">
          <Button
            size="lg"
            asChild
            className="bg-foreground text-background hover:bg-foreground/90 gap-2 shadow-lg"
          >
            <a
              href={SOCIAL.instagram.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCta("home_content_instagram_follow", SOCIAL.instagram.profileUrl, "Follow on Instagram")}
            >
              <Instagram className="w-5 h-5" />
              Follow on Instagram
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
};